// Originación (`/credit/origination`) — Portal Admin SureKeep.
// Mockup: `surekeep/mockups/backoffice/portal-admin-sk-originacion.html`.
//
// Conectada a `GET /backoffice/origination/analytics` (`retail-wizard-business`), que es SILO-WIDE:
// no hay selector de cadena y los tres chips de ventana viajan en la misma respuesta.
//
// ── Lo que el mock pinta y NO se construye todavía ───────────────────────────────────────────────
// · **Aprobación del motor** (el cuarto indicador y su columna del ranking): las evaluaciones del
//   motor solo se pueden consultar por acreditado, así que no hay cómo leerlas por ventana. Se
//   OMITE — una columna de guiones se lee como «esta tienda no aprueba nada».
// · **Conversión** y **rechazos por motivo**: necesitan las solicitudes de crédito con su motivo de
//   rechazo, que son dominio de la pista SOFOM. La tercera regla de anomalías sale de ahí.
// · La **mezcla por segmento** que el mock pinta bajo «Cómo originamos»: el segmento no existe en el
//   modelo de crédito. No se construye ni se inventa; la sección de niveles ocupa ese espacio.
// · El **drill-down a vendedores** del ranking.
import { useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { ErrorState, Select, Skeleton } from '@/kit';
import { ScreenBand, ScreenBody } from '@/shared/patterns/ScreenBand';
import { ScreenErrorBoundary } from '@/shared/patterns/ScreenErrorBoundary';
import { ApiError, userMessageOf } from '@/api/errors';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';
import {
    ORIGINATION_RANGE_KEYS,
    ORIGINATION_RANGE_LABEL,
    type FirstInstallmentCohortKpi,
    type OriginationRangeKey,
    type OriginationRiskMix,
} from '@/api/services/retail-wizard/backoffice';
import { useOriginationAnalytics } from './api/useOrigination';
import { resolveOriginationAbilities } from './permissions';
import { OriginationAnomalies } from './components/OriginationAnomalies';
import { OriginationCard } from './components/OriginationCard';
import { CaptureDeltaChip, CohortDeltaChip, VolumeDeltaChip } from './components/OriginationDeltas';
import { OriginationRanking } from './components/OriginationRanking';
import { OriginationRiskPanel } from './components/OriginationRiskPanel';
import { OriginationTrendChart, OriginationTrendLegend } from './components/OriginationTrendChart';
import { originationAnomalies } from './lib/originationCohort';
import {
    formatPct,
    formatSci,
    formatUnits,
    formatWindow,
    hasRiskMix,
    moneyShort,
    summarizeChannels,
    type RankingColumn,
    type SortDirection,
    type TrendMode,
} from './lib/originationMetrics';

const PERMISSION_DENIED = 'PERMISSION_DENIED';

const NO_PERMISSION =
    'Necesitas el permiso de lectura de originación para ver este tablero. Si crees que es un error, contacta al administrador.';

/** Lo que el mock pinta y todavía no tiene de dónde salir. Se dice; no se pinta en cero. */
const PENDING_BLOCKS =
    'Aprobación del motor —el indicador y su columna del ranking—, la conversión de la originación, los rechazos por motivo y el detalle por vendedor todavía no tienen dato en el sistema: no se pintan hasta que exista. Por lo mismo, de las tres reglas de anomalías del mockup solo corren dos: la que mira un motivo de rechazo que sube no se está evaluando. La mezcla por segmento que el mockup pone bajo «Cómo originamos» tampoco se pinta: el segmento no existe en el modelo de crédito y no se va a inventar.';

/** El cuerpo de una card cuando no hay nada que pintar. Un vacío sin explicación se lee como roto. */
function EmptyBlock({ children }: { children: React.ReactNode }) {
    return (
        <p className="p-3 text-center text-[11px] italic leading-relaxed text-muted-foreground">
            {children}
        </p>
    );
}

/** Un indicador del bloque de la derecha (`.kpi-item` del mock). */
function Kpi({
    label,
    value,
    delta,
    hint,
    className,
}: {
    label: string;
    value: string;
    delta: React.ReactNode;
    hint: React.ReactNode;
    className?: string;
}) {
    return (
        <div className={cn('flex min-w-0 flex-col justify-center px-3.5 py-2.5', className)}>
            <span className="mb-1 text-[9.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                {label}
            </span>
            <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-2xl font-bold leading-[1.05] tracking-[-0.01em] tabular-nums">
                    {value}
                </span>
                {delta}
            </div>
            <span className="mt-1 text-[11px] leading-[1.4] text-muted-foreground">{hint}</span>
        </div>
    );
}

export function OriginationPage() {
    const { permissions } = useAuth();
    const abilities = useMemo(() => resolveOriginationAbilities(permissions), [permissions]);

    const [range, setRange] = useState<OriginationRangeKey>('7d');
    // La tienda resaltada en el scatter. Es un TOGGLE, como el `selectTienda()` del mock.
    const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
    const [trendMode, setTrendMode] = useState<TrendMode>('units');
    const [sort, setSort] = useState<{ column: RankingColumn; direction: SortDirection }>({
        column: 'sureKeepUnits',
        direction: 'desc',
    });

    const query = useOriginationAnalytics(abilities.canViewAnalytics);
    const data = query.data;
    const blocks = data?.ranges[range];

    // El KPI de captura trae el total y el de SureKeep, pero no cómo se parte el resto. La serie
    // diaria sí, y es la misma ventana.
    const channels = useMemo(() => summarizeChannels(blocks?.daily ?? []), [blocks]);

    // Las anomalías salen del ranking de la ventana: mismas tiendas, mismos números que la tabla.
    const anomalies = useMemo(
        () => originationAnomalies(blocks?.storeRanking ?? []),
        [blocks?.storeRanking],
    );

    // C5 y el mínimo del enganche salen de las MISMAS dos fuentes: los créditos y el catálogo de
    // planes. Si cualquiera falló, el cuadro se oculta y la pantalla dice por qué.
    const riskSourcesReady = (data?.riskMixAvailable ?? false) && (data?.plansAvailable ?? false);
    const storeScatter = blocks?.storeScatter ?? [];
    const showRiskCard =
        riskSourcesReady && (hasRiskMix(blocks?.riskMix) || storeScatter.length > 0);

    const toggleStore = (storeId: string) => {
        setSelectedStoreId((current) => (current === storeId ? null : storeId));
    };

    const toggleSort = (column: RankingColumn) => {
        setSort((current) =>
            current.column === column
                ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                : { column, direction: column === 'storeName' ? 'asc' : 'desc' },
        );
    };

    const forbidden = query.error instanceof ApiError && query.error.code === PERMISSION_DENIED;

    return (
        <div className="flex h-full min-h-0 flex-col">
            {/* El chip de ventana. El mock lo pone en el topbar; el shell de este portal solo deja
                escribir ahí desde la ruta de layout, así que vive en la banda de pantalla —el mismo
                lugar donde «Análisis de ventas» pone su selector de rango. */}
            <ScreenBand className="text-[11px]">
                <span className="flex-none text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Ventana
                </span>
                <Select
                    value={range}
                    onChange={(value) => {
                        setRange(value as OriginationRangeKey);
                        // Otra ventana es otro ranking: la tienda resaltada puede ni estar.
                        setSelectedStoreId(null);
                    }}
                    options={ORIGINATION_RANGE_KEYS.map((key) => ({
                        value: key,
                        label: ORIGINATION_RANGE_LABEL[key],
                    }))}
                    className="h-[30px] w-[110px] flex-none text-[11.5px]"
                />
                <div className="flex-1" />
                <span className="flex-none text-[10.5px] text-muted-foreground">
                    Δ contra la ventana anterior
                    {blocks ? ` · ${formatWindow(blocks.from, blocks.to)}` : ''}
                    {data ? ` · ${formatUnits(data.retailersScanned)} cadenas barridas` : ''}
                </span>
                {/* Los datos se cachean 5 minutos: sin esto nadie tiene cómo pedir la foto nueva. */}
                <button
                    type="button"
                    onClick={() => {
                        void query.refetch();
                    }}
                    disabled={query.isFetching || !abilities.canViewAnalytics}
                    className="flex size-6 flex-none items-center justify-center rounded border border-border text-muted-foreground transition-colors hover:bg-accent/50 disabled:opacity-50"
                    title="Actualizar los datos"
                    aria-label="Actualizar los datos"
                >
                    <RefreshCw
                        className={cn('size-3', query.isFetching && 'animate-spin')}
                        aria-hidden
                    />
                </button>
                <span className="sr-only" role="status" aria-live="polite">
                    {query.isFetching ? 'Actualizando los datos…' : ''}
                </span>
            </ScreenBand>

            <ScreenBody>
                {/* El boundary envuelve SOLO el contenido: un dato malformado no debe llevarse el
                    sidebar ni el topbar, que son la salida del operador. */}
                <ScreenErrorBoundary
                    resetKeys={[range, data]}
                    title="No se pudo mostrar el tablero de originación"
                >
                    {!abilities.canViewAnalytics || forbidden ? (
                        <ErrorState
                            title="No tienes acceso a Originación"
                            description={NO_PERMISSION}
                        />
                    ) : query.isPending ? (
                        <div className="grid grid-cols-2 gap-2.5">
                            {[0, 1, 2, 3].map((index) => (
                                <div
                                    key={index}
                                    className="rounded-md border border-border bg-card p-3"
                                >
                                    <Skeleton lines={5} />
                                </div>
                            ))}
                        </div>
                    ) : query.isError || !data ? (
                        <ErrorState
                            title="No se pudo cargar el tablero de originación"
                            description={userMessageOf(query.error)}
                            onRetry={() => {
                                void query.refetch();
                            }}
                        />
                    ) : (
                        <div className="grid grid-cols-2 gap-2.5">
                            {/* Los avisos van ARRIBA: cambian cómo se leen TODOS los números de
                                abajo, así que verlos después sería tarde. */}
                            {!data.rosterAvailable ? (
                                <Notice tone="danger">
                                    El padrón de cadenas no respondió. Este tablero está{' '}
                                    <strong className="font-semibold">incompleto</strong>: los
                                    números de abajo cubren solo las cadenas que sí se pudieron
                                    leer, y lo que falta no es «cero ventas», es «no se leyó».
                                </Notice>
                            ) : null}
                            {!riskSourcesReady ? (
                                <Notice tone="danger">
                                    {!data.riskMixAvailable && !data.plansAvailable
                                        ? 'Ni los créditos ni el catálogo de planes respondieron.'
                                        : data.riskMixAvailable
                                          ? 'El catálogo de planes no respondió.'
                                          : 'Los créditos de la ventana no se pudieron leer.'}{' '}
                                    Sin eso no hay cómo separar al empleado del nivel de riesgo ni
                                    decir qué enganche mínimo exigía cada plan:{' '}
                                    <strong className="font-semibold">
                                        el cuadro «Cómo originamos» y el Δ del enganche no se pintan
                                    </strong>
                                    . Están <strong className="font-semibold">degradados</strong>,
                                    no en cero — vuelve a cargar en un momento.
                                </Notice>
                            ) : null}
                            {!data.firstInstallmentCohortAvailable ? (
                                <Notice tone="warning">
                                    El motor no respondió el estado de la primera cuota. La{' '}
                                    <strong className="font-semibold">
                                        cosecha viaja vacía y sale como N/D
                                    </strong>{' '}
                                    en el indicador, en la columna del ranking y en el scatter — eso
                                    es <strong className="font-semibold">degradado</strong>, no «las
                                    tiendas no pagan». El resto del tablero está intacto.
                                </Notice>
                            ) : null}
                            {data.cohortPreviousTruncated ? (
                                <Notice tone="warning">
                                    La ventana anterior se cortó al leer la cosecha: el Δ del
                                    indicador «1ª cuota a 14d» salió de una parte de los créditos.
                                    No lo leas como la variación real.
                                </Notice>
                            ) : null}
                            {data.creditsTruncated ? (
                                <Notice tone="warning">
                                    La ventana tenía más créditos que el tope de lectura. El cuadro
                                    «Cómo originamos» y el Δ del enganche salieron de{' '}
                                    <strong className="font-semibold">una parte</strong> de los
                                    créditos, no del total: léelos como una muestra.
                                </Notice>
                            ) : null}
                            {data.truncatedCurrent || data.truncatedPrevious ? (
                                <Notice tone="warning">
                                    El silo cortó la lectura{' '}
                                    {data.truncatedCurrent && data.truncatedPrevious
                                        ? 'de las dos ventanas'
                                        : data.truncatedCurrent
                                          ? 'de la ventana actual'
                                          : 'de la ventana anterior'}
                                    . Las variaciones contra la ventana anterior no son confiables —
                                    no las leas como aproximadas.
                                </Notice>
                            ) : null}

                            {/* Debajo de los avisos: primero se sabe si el tablero es confiable y
                                después qué salió de patrón. */}
                            <OriginationAnomalies
                                anomalies={anomalies}
                                onSelectStore={toggleStore}
                            />

                            {!blocks ? (
                                <div className="col-span-2">
                                    <ErrorState
                                        title="El silo no devolvió esta ventana"
                                        description={`La respuesta no trae el bloque de ${ORIGINATION_RANGE_LABEL[range]}. Elige otra ventana o vuelve a cargar.`}
                                        onRetry={() => {
                                            void query.refetch();
                                        }}
                                    />
                                </div>
                            ) : (
                                <>
                                    {/* 1 · Tendencia día por día ──────────────────────────────── */}
                                    <OriginationCard
                                        title="Tendencia día por día"
                                        help="trend"
                                        meta={
                                            <span className="inline-flex items-center gap-2">
                                                <span className="inline-flex rounded-[5px] border border-border bg-background p-0.5">
                                                    {(
                                                        [
                                                            ['units', 'Créditos'],
                                                            ['cents', 'Pesos'],
                                                        ] as const
                                                    ).map(([mode, label]) => (
                                                        <button
                                                            key={mode}
                                                            type="button"
                                                            aria-pressed={trendMode === mode}
                                                            onClick={() => {
                                                                setTrendMode(mode);
                                                            }}
                                                            className={cn(
                                                                'rounded-[3px] px-2.5 py-0.5 text-[10.5px] tabular-nums transition-colors',
                                                                trendMode === mode
                                                                    ? 'bg-primary font-semibold text-primary-foreground'
                                                                    : 'text-muted-foreground hover:text-foreground',
                                                            )}
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </span>
                                                <span>
                                                    Últimos {formatUnits(blocks.daily.length)} días
                                                </span>
                                            </span>
                                        }
                                        className="h-[235px]"
                                    >
                                        {blocks.daily.length === 0 ? (
                                            <EmptyBlock>
                                                El silo no devolvió ningún día para esta ventana.
                                            </EmptyBlock>
                                        ) : (
                                            <>
                                                <OriginationTrendChart
                                                    daily={blocks.daily}
                                                    mode={trendMode}
                                                />
                                                <OriginationTrendLegend />
                                            </>
                                        )}
                                    </OriginationCard>

                                    {/* 2 · Indicadores de la ventana ──────────────────────────── */}
                                    <OriginationCard
                                        title="Indicadores de la ventana"
                                        help="indicators"
                                        meta="vs ventana anterior"
                                        className="h-[235px]"
                                    >
                                        <div className="grid h-full grid-cols-2 grid-rows-2">
                                            <Kpi
                                                className="border-b border-r border-border/50"
                                                label="Volumen originado SK"
                                                value={formatUnits(blocks.volume.units)}
                                                delta={
                                                    <VolumeDeltaChip
                                                        changePct={blocks.volume.unitsChangePct}
                                                        current={blocks.volume.units}
                                                        previous={blocks.volume.previousUnits}
                                                    />
                                                }
                                                hint={
                                                    <>
                                                        {formatUnits(blocks.volume.units)} créditos
                                                        ·{' '}
                                                        <span className="tabular-nums">
                                                            {moneyShort(blocks.volume.cents)}
                                                        </span>{' '}
                                                        financiados
                                                    </>
                                                }
                                            />
                                            <Kpi
                                                className="border-b border-border/50"
                                                label="Captura del mercado"
                                                value={formatPct(blocks.marketCapture.capturePct)}
                                                delta={
                                                    <CaptureDeltaChip
                                                        changePp={blocks.marketCapture.changePp}
                                                    />
                                                }
                                                hint={
                                                    blocks.marketCapture.capturePct === null
                                                        ? 'Sin ventas en la ventana: no hay base para calcular la captura'
                                                        : `${formatUnits(channels.otherFinanciersUnits)} con otras financieras · ${formatUnits(channels.cashUnits)} de contado`
                                                }
                                            />
                                            {/* 🔴 Sin elegibles es N/D, jamás un 0 %: el cero diría
                                                que TODOS fallaron, que es otra cosa. */}
                                            <Kpi
                                                className="col-span-2"
                                                label="Cosecha: 1ª cuota a 14d"
                                                value={cohortValue(blocks.firstInstallmentCohort)}
                                                delta={
                                                    blocks.firstInstallmentCohort
                                                        .eligibleCredits === 0 ? null : (
                                                        <CohortDeltaChip
                                                            changePp={
                                                                blocks.firstInstallmentCohort
                                                                    .changePp
                                                            }
                                                            previousEligibleCredits={
                                                                blocks.firstInstallmentCohort
                                                                    .previousEligibleCredits
                                                            }
                                                        />
                                                    )
                                                }
                                                hint={cohortHint(blocks.firstInstallmentCohort)}
                                            />
                                        </div>
                                    </OriginationCard>

                                    {/* 3 · Cómo originamos (C5) ───────────────────────────────── */}
                                    {showRiskCard ? (
                                        <OriginationCard
                                            title="Cómo originamos"
                                            help="howWeOriginate"
                                            meta={riskMeta(blocks.riskMix)}
                                            className="h-[330px]"
                                        >
                                            <OriginationRiskPanel
                                                mix={blocks.riskMix}
                                                storeScatter={blocks.storeScatter}
                                                selectedStoreId={selectedStoreId}
                                                onSelectStore={toggleStore}
                                            />
                                        </OriginationCard>
                                    ) : null}

                                    {/* 4 · Ranking por tienda ─────────────────────────────────── */}
                                    <OriginationCard
                                        title="Ranking por tienda"
                                        help="ranking"
                                        // Es un RANKING: el orden por defecto es el volumen, no el
                                        // alfabético. La columna Tienda ordena por nombre.
                                        meta={`${formatUnits(blocks.storeRanking.length)} tiendas con venta en la ventana`}
                                        // Sin C5 al lado la tabla ocupa el ancho entero: media
                                        // pantalla vacía se lee como un cuadro que no cargó.
                                        wide={!showRiskCard}
                                        className={showRiskCard ? 'h-[330px]' : undefined}
                                    >
                                        {blocks.storeRanking.length === 0 ? (
                                            <EmptyBlock>
                                                Ninguna tienda del silo registró ventas en esta
                                                ventana.
                                            </EmptyBlock>
                                        ) : (
                                            <div className="min-h-0 flex-1 overflow-auto">
                                                <OriginationRanking
                                                    rows={blocks.storeRanking}
                                                    sort={sort}
                                                    onSort={toggleSort}
                                                    selectedStoreId={selectedStoreId}
                                                    onSelectStore={toggleStore}
                                                />
                                            </div>
                                        )}
                                    </OriginationCard>

                                    <p className="col-span-2 text-[10.5px] leading-relaxed text-muted-foreground">
                                        {PENDING_BLOCKS}
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </ScreenErrorBoundary>
            </ScreenBody>
        </div>
    );
}

/** Un aviso de página, arriba del grid. `danger` para lo que invalida el tablero entero. */
function Notice({ tone, children }: { tone: 'warning' | 'danger'; children: React.ReactNode }) {
    return (
        <div
            className={cn(
                'col-span-2 flex items-start gap-1.5 rounded-md border px-2.5 py-1.5 text-[10.5px] font-medium text-foreground',
                tone === 'danger'
                    ? 'border-destructive bg-destructive/10'
                    : 'border-warning bg-warning/15',
            )}
        >
            <AlertTriangle
                className={cn(
                    'mt-px size-3 flex-none',
                    tone === 'danger' ? 'text-destructive' : 'text-warning',
                )}
                aria-hidden
            />
            <span>{children}</span>
        </div>
    );
}

/** El valor del KPI de cosecha. Sin elegibles es `N/D`, que NO es lo mismo que un `0 %`. */
function cohortValue(cohort: FirstInstallmentCohortKpi): string {
    return cohort.eligibleCredits === 0 ? 'N/D' : formatPct(cohort.cohortPct);
}

/** El subtexto del KPI: el divisor de la cosecha, o por qué no hay divisor. */
function cohortHint(cohort: FirstInstallmentCohortKpi): string {
    if (cohort.eligibleCredits === 0) {
        return 'Sin créditos con la antigüedad suficiente: ninguno cumplió los 14 días todavía';
    }
    return `${formatUnits(cohort.eligibleCredits)} ${cohort.eligibleCredits === 1 ? 'crédito elegible' : 'créditos elegibles'} (originados hace 14 días o más) · ${formatUnits(cohort.paidCredits)} pagaron`;
}

/**
 * La meta del cuadro C5: créditos con nivel, SCI promedio y si la nube salió recortada.
 * Dice «sin empleados» porque el silo los excluye del promedio, y el número sin eso engaña.
 */
function riskMeta(mix: OriginationRiskMix): string {
    const parts = [`${formatUnits(mix.totalLeveled)} créditos con nivel`];
    if (mix.sciAvg !== null) parts.push(`SCI prom. ${formatSci(mix.sciAvg)} sin empleados`);
    return parts.join(' · ');
}
